/**
 * UniversalFilterBar — auto-configures the FieldFilterBar from the backend
 * schema endpoint. Just drop it into any module page and every schema field
 * becomes filterable.
 *
 * Usage:
 *   <UniversalFilterBar moduleKey="orders" value={filters} onChange={setFilters} />
 *
 * Every field type comes from `/api/settings/schema/{module}` (backend
 * derives the type from the field name + settings.dropdowns).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import FieldFilterBar from "./FieldFilterBar";

// Cache schemas across mounts — module schemas rarely change per session.
const _cache = new Map();

export default function UniversalFilterBar({ moduleKey, value, onChange }) {
  const [fields, setFields] = useState(_cache.get(moduleKey)?.fields || []);
  const [dropdowns, setDropdowns] = useState(_cache.get("_dropdowns") || {});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cached = _cache.get(moduleKey);
        if (!cached) {
          const { data } = await api.get(`/settings/schema/${moduleKey}`);
          const derived = (data.fields || []).map((f) => ({
            key: f.key,
            label: f.label,
            type: f.type,
            opts: f.type === "select" ? f.options : undefined,
          }));
          _cache.set(moduleKey, { fields: derived });
          if (!cancelled) setFields(derived);
        }
        if (!_cache.get("_dropdowns")) {
          const { data: dd } = await api.get("/settings/dropdowns");
          _cache.set("_dropdowns", dd);
          if (!cancelled) setDropdowns(dd);
        }
      } catch {
        // Silent — page will fall back to no filter bar
      }
    })();
    return () => { cancelled = true; };
  }, [moduleKey]);

  if (!fields.length) return null;
  return (
    <FieldFilterBar
      moduleKey={moduleKey}
      fields={fields}
      dropdowns={dropdowns}
      value={value}
      onChange={onChange}
    />
  );
}
